import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Camera, ImagePlus, Trash2, Loader2, Eye, CloudOff, Star } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import { queuePhoto, listPendingPhotos } from '../lib/offlineQueue';
import { compressImages } from '../lib/imageCompress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Internal — fetches an authenticated image once and yields a blob URL.
 * Without this, raw `<img src="…/api/…">` requests would lack the
 * Authorization header and return 401, leaving thumbnails blank.
 */
const useAuthedImage = (photoId) => {
  const { t } = useTranslation();
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!photoId) return undefined;
    let revoked = false;
    let objectUrl = null;
    const controller = new AbortController();
    (async () => {
      try {
        const resp = await axios.get(`${API}/entity-photos/${photoId}`, {
          responseType: 'blob',
          signal: controller.signal,
        });
        if (revoked) return;
        objectUrl = URL.createObjectURL(resp.data);
        setSrc(objectUrl);
      } catch {
        // Silent — broken thumbnail simply won't display
      }
    })();
    return () => {
      revoked = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photoId]);

  return src;
};

const PhotoThumb = ({ photo, readOnly, onPreview, onDelete, isMain, onSelectMain }) => {
  const { t } = useTranslation();
  const src = useAuthedImage(photo.id);
  return (
    <div className={`relative group h-14 w-14 rounded-md overflow-hidden border bg-slate-100 flex items-center justify-center ${isMain ? 'border-2 border-[#AA0000]' : 'border-slate-200'}`}>
      {src ? (
        <img
          src={src}
          alt={photo.original_filename || photo.filename}
          className="w-full h-full object-cover cursor-zoom-in"
          onClick={() => onPreview(src)}
          data-testid={`photo-thumb-${photo.id}`}
        />
      ) : (
        <Loader2 className="h-3 w-3 text-slate-400 animate-spin" />
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
        {src && (
          <button
            type="button"
            onClick={() => onPreview(src)}
            className="p-1 rounded bg-white/90 hover:bg-white text-slate-700"
            title={t('auto.misc2.preview')}
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        )}
        {!readOnly && onSelectMain && (
          <button
            type="button"
            onClick={() => onSelectMain(isMain ? null : photo.id)}
            className={`p-1 rounded bg-white/90 hover:bg-white ${isMain ? 'text-[#AA0000]' : 'text-slate-700'}`}
            title={isMain ? t('auto.misc2.publicPhotoClickToUnset') : t('auto.misc2.makeThisThePublicPhoto')}
            data-testid={`photo-main-${photo.id}`}
          >
            <Star className={`h-3.5 w-3.5 ${isMain ? 'fill-current' : ''}`} />
          </button>
        )}
        {!readOnly && (
          <button
            type="button"
            onClick={() => onDelete(photo)}
            className="p-1 rounded bg-white/90 hover:bg-white text-red-600"
            title={t('auto.misc2.delete')}
            data-testid={`photo-delete-${photo.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Reusable photo gallery for any entity sub-item.
 *
 * Props:
 *   entityType: 'checklist' | 'test_report' | 'ncr' | 'audit_finding'
 *   entityId:   the parent entity's UUID
 *   itemKey:    sub-item identifier (e.g. "0.3" for section 0 / item 3)
 *               null/undefined means "attached to the entity as a whole"
 *   readOnly:   hide upload/delete affordances (default false)
 *   compact:    smaller thumbnails (default false)
 *   onCountChange(count): optional callback whenever photo count changes
 */
const EntityPhotoGallery = ({
  entityType,
  entityId,
  itemKey,
  readOnly = false,
  compact = false,
  onCountChange,
  mainPhotoId,
  onSelectMain,
}) => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pending, setPending] = useState([]);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Photos taken with no signal, shown alongside the uploaded ones so the
  // engineer can see their evidence was captured.
  const loadPending = useCallback(async () => {
    try {
      const rows = await listPendingPhotos(entityType, entityId, itemKey);
      setPending(rows.map((r) => ({ ...r, url: URL.createObjectURL(r.blob) })));
    } catch { setPending([]); }
  }, [entityType, entityId, itemKey]);

  useEffect(() => {
    loadPending();
    const onChanged = () => loadPending();
    window.addEventListener('offline-queue-changed', onChanged);
    return () => window.removeEventListener('offline-queue-changed', onChanged);
  }, [loadPending]);

  // Held in a ref on purpose: callers pass an inline arrow, so depending on
  // it directly made every parent re-render refetch the photos, which called
  // back into the parent — a loop that flickered the whole dialog.
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const fetchPhotos = useCallback(async () => {
    if (!entityType || !entityId) return;
    setLoading(true);
    try {
      const params = itemKey != null ? { item_key: itemKey } : {};
      const resp = await axios.get(
        `${API}/entity-photos/${entityType}/${entityId}`,
        { params }
      );
      setPhotos(resp.data || []);
      onCountChangeRef.current?.((resp.data || []).length);
    } catch (e) {
      setPhotos([]);
      onCountChangeRef.current?.(0);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, itemKey]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const uploadFiles = async (rawFiles) => {
    if (!rawFiles?.length) return;
    setUploading(true);
    // Shrink before anything else so both the upload and the offline
    // queue carry the small version.
    const fileList = await compressImages(rawFiles);
    let successCount = 0;
    let queuedCount = 0;
    for (const file of fileList) {
      const queueIt = async () => {
        await queuePhoto({
          entityType, entityId, itemKey, file,
          token: localStorage.getItem('token'),
        });
        queuedCount++;
      };
      try {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          await queueIt();
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        if (itemKey != null) fd.append('item_key', String(itemKey));
        await axios.post(
          `${API}/entity-photos/${entityType}/${entityId}`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        successCount++;
      } catch (e) {
        // No response at all means the network died, not a rejection —
        // hold the photo on the device rather than losing the evidence.
        if (!e.response) {
          try { await queueIt(); continue; } catch { /* fall through */ }
        }
        toast.error(e.response?.data?.detail || `Failed to upload "${file.name}"`);
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} photo${successCount > 1 ? 's' : ''} uploaded`);
      fetchPhotos();
    }
    if (queuedCount > 0) {
      toast.success(`${queuedCount} photo${queuedCount > 1 ? 's' : ''} saved on this device — they'll upload when you're back online`);
      loadPending();
    }
    setUploading(false);
  };

  const handleFileInput = (e) => {
    uploadFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const handleDelete = async (photo) => {
    if (!window.confirm(t('auto.misc2.deleteThisPhoto'))) return;
    try {
      await axios.delete(`${API}/entity-photos/${photo.id}`);
      toast.success(t('auto.misc2.photoDeleted'));
      fetchPhotos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-2" data-testid={`photo-gallery-${entityType}-${itemKey ?? 'root'}`}>
      <div className="flex flex-wrap items-center gap-2">
        {photos.map((p) => (
          <PhotoThumb
            key={p.id}
            photo={p}
            readOnly={readOnly}
            onPreview={setPreviewUrl}
            onDelete={handleDelete}
            isMain={!!mainPhotoId && mainPhotoId === p.id}
            onSelectMain={onSelectMain}
          />
        ))}

        {pending.map((p) => (
          <div
            key={`pending-${p.id}`}
            className={`relative ${compact ? 'h-14 w-14' : 'h-20 w-20'} shrink-0 overflow-hidden rounded border-2 border-dashed border-slate-400 bg-slate-50`}
            title={t('auto.misc2.waitingToUploadSavedOnThis')}
            data-testid={`photo-pending-${entityType}-${itemKey ?? 'root'}-${p.id}`}
          >
            <img src={p.url} alt={p.name} className="h-full w-full object-cover opacity-70" />
            <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-0.5 bg-slate-800/80 py-0.5 text-[9px] font-medium text-white">
              <CloudOff className="h-2.5 w-2.5" />{t('auto.misc2.queued')}
            </span>
          </div>
        ))}

        {!readOnly && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleFileInput}
              data-testid={`photo-file-input-${entityType}-${itemKey ?? 'root'}`}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={handleFileInput}
              data-testid={`photo-camera-input-${entityType}-${itemKey ?? 'root'}`}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className={`${compact ? 'h-14' : 'h-20'} flex flex-col items-center justify-center gap-1 px-3`}
              data-testid={`photo-upload-${entityType}-${itemKey ?? 'root'}`}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <ImagePlus className="h-4 w-4" />
                  <span className="text-[10px]">{t('auto.misc2.add')}</span>
                </>
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploading}
              className={`${compact ? 'h-14' : 'h-20'} flex flex-col items-center justify-center gap-1 px-3 sm:hidden`}
              data-testid={`photo-camera-${entityType}-${itemKey ?? 'root'}`}
              title={t('auto.misc2.takePhoto')}
            >
              <Camera className="h-4 w-4" />
              <span className="text-[10px]">{t('auto.misc2.camera')}</span>
            </Button>
          </>
        )}
      </div>

      {!readOnly && photos.length === 0 && !loading && !compact && (
        <p className="text-xs text-slate-400 italic">
          No photos yet — click <em>{t('auto.misc2.add')}</em> {t('auto.misc2.toUploadOr')} <em>{t('auto.misc2.camera')}</em> on mobile to take a picture.
        </p>
      )}

      <Dialog open={!!previewUrl} onOpenChange={(open) => !open && setPreviewUrl(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black">
          {previewUrl && (
            <img
              src={previewUrl}
              alt={t('auto.misc2.preview')}
              className="w-full h-auto max-h-[85vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EntityPhotoGallery;
